import type { FC, SVGProps } from "react"
import { useCallback, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate, useParams } from "@tanstack/react-router"
import {
	IconCodeBranchFill18 as CodeBranchIcon,
	IconCopyIdFill18 as CopyIdIcon,
	IconLink2Fill18 as LinkIcon,
} from "nucleo-ui-fill-18"
import {
	IconCopyOutline18 as CopyMenuIcon,
	IconArrowsExpandDiagonal4Outline18 as ExpandIcon,
	IconMoveToRightOutline18 as MoveToPortalIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import {
	Button,
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	Empty,
	IconButton,
	IssueStatusIcon,
	LinearIcon,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@productlane/ui"
import { ClaudeIcon } from "@productlane/ui/src/icons/project/claude-icon"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { queries } from "@productlane/zero/queries"

import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
	DetailThreadsList,
	hasActiveDetailFilters,
	useDetailThreadsControls,
} from "@/components/detail-screen"
import { DetailThreadsToolbar } from "@/components/detail-screen/detail-threads-toolbar"
import { DetailUpvotesList } from "@/components/detail-screen/detail-upvotes-list"
import { countUpvotes } from "@/components/detail-screen/upvote-count"
import { useDetailListState } from "@/components/detail-screen/use-detail-list-state"
import { MetaInfo } from "@/components/meta-info"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import {
	DescriptionSwitch,
	TitleTabsSwitcher,
	useTitleTab,
} from "@/components/shared/description-switch"
import { NotifyRecipients } from "@/components/shared/notify-recipients"
import { RoleCheck } from "@/components/shared/role-check"
import { showsPortalVisibility } from "@/components/shared/visibility-labels"
import { VisibilityToggle } from "@/components/shared/visibility-toggle"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import {
	useBackNavigation,
	usePreviousNavEntry,
	useSetBackNavigation,
} from "@/lib/back-navigation"
import { detailPath } from "@/lib/detail-drawer"
import { openDeepLink } from "@/lib/open-deep-link"
import { isQuerySettled } from "@/lib/query-settled"
import { useZeroWorkspace } from "@/lib/zero"
import { MoveRequestToPortalDialog } from "@/screens/app/roadmap/move-requests-to-portal-dialog"
import {
	getLanguageByCodeWithFallback,
	LanguageSelector,
	useSelectedLanguage,
} from "@/screens/settings/settings-pages/language-selector"
import { HowItWorksButton } from "@/screens/settings/settings-switches/how-it-works-button"
import { BreadcrumbSlash, DetailBreadcrumb, DetailTopBar } from "../page-header"
import { SubIssuesList } from "./sub-issues-list"

type DetailsTabsType = "requests" | "upvotes"

export const IssuesDetailScreen = () => {
	const params = useParams({ strict: false })
	const id = params.id!
	useSetBackNavigation({
		type: "issues-detail",
		url: `/issues/${id}`,
	})

	return <IssueDetailView id={id} />
}

interface IssueDetailViewProps {
	id: string
	variant?: "page" | "drawer"
	onClose?: () => void
}

export const IssueDetailView = ({
	id,
	variant = "page",
	onClose,
}: IssueDetailViewProps) => {
	const isDrawer = variant === "drawer"

	const threadsState = useDetailListState()

	const threadControls = useDetailThreadsControls()
	const [teamMembers] = useQuery(queries.teamMembers())

	const [issue, issueResult] = useQuery(queries.issueDetail(id ?? ""))
	const workspace = useZeroWorkspace()
	const [selectedLanguage, setSelectedLanguage] = useSelectedLanguage()
	// Translations only matter on a published portal; otherwise edit the default language.
	const editorLanguage = workspace?.domainPublic
		? selectedLanguage
		: getLanguageByCodeWithFallback(workspace?.defaultLanguage)
	const hasPublicFeatureRequests = !!workspace?.domainPublic
	const [titleTab, setTitleTab] = useTitleTab(hasPublicFeatureRequests)
	const navigate = useNavigate()

	const claudeCodeLink = api.codingAgent.buildClaudeCodeLink.useQuery(
		{ issueId: id },
		{ enabled: !!id, retry: false },
	)

	const openInClaudeCode = useCallback(() => {
		if (claudeCodeLink.error) {
			toast.error({
				title: "No repository configured",
				description:
					"Set your repository in the Cursor integration to open issues in Claude Code.",
				action: {
					label: "Configure",
					callback: () =>
						void navigate({ to: "/settings/integrations/cursor" }),
				},
			})
			return
		}
		const data = claudeCodeLink.data
		if (!data) return
		analytics.track("open-issue-in-claude-code", { id })
		openDeepLink(data.deepLink)
		toast.success({
			title: "Opening in Claude Code",
			description: data.branchName ? `Branch ${data.branchName}` : undefined,
			descriptionAction: {
				label: "Copy prompt",
				callback: () => void navigator.clipboard.writeText(data.prompt),
			},
		})
	}, [claudeCodeLink.data, claudeCodeLink.error, id, navigate])

	const { goBack } = useBackNavigation()
	const previousEntry = usePreviousNavEntry("issues-detail")
	const close = useCallback(
		(shortcut = false) => {
			if (!issue) return
			analytics.track("issue-close", { id: issue.id, shortcut })
			if (isDrawer) {
				onClose?.()
				return
			}
			goBack(`/issues${issue.status === "completed" ? "?state=closed" : ""}`)
		},
		[goBack, isDrawer, issue, onClose],
	)

	const copyIdentifier = useCallback(() => {
		if (!issue?.identifier) return
		void navigator.clipboard.writeText(issue.identifier)
		analytics.track("issue-copy-identifier", { id: issue.id })
		toast.success({ title: `Copied ${issue.identifier}`, icon: CopyIdIcon })
	}, [issue?.id, issue?.identifier])

	const copyUrl = useCallback(() => {
		if (!issue?.url) return
		void navigator.clipboard.writeText(issue.url)
		analytics.track("issue-copy-url", { id: issue.id })
		toast.success({ title: "Copied issue link", icon: LinkIcon })
	}, [issue?.id, issue?.url])

	const copyBranchName = useCallback(() => {
		const branchName = claudeCodeLink.data?.branchName
		if (!branchName) return
		void navigator.clipboard.writeText(branchName)
		analytics.track("issue-copy-branch-name", { id })
		toast.success({ title: `Copied ${branchName}`, icon: CodeBranchIcon })
	}, [claudeCodeLink.data?.branchName, id])

	useShortcut("ctrl+period, meta+period", () => copyIdentifier(), {
		preventDefault: true,
	})

	useShortcut("ctrl+shift+comma, meta+shift+comma", () => copyUrl(), {
		preventDefault: true,
	})

	useShortcut("ctrl+shift+period, meta+shift+period", () => copyBranchName(), {
		preventDefault: true,
	})

	useShortcut(
		"l",
		() => {
			issue?.url && window.open(issue.url, "_blank")
		},
		{ preventDefault: true },
	)

	useShortcut(
		"c",
		() => {
			openInClaudeCode()
		},
		{ preventDefault: true },
	)

	// The drawer is a Radix dialog, so Escape is already wired to onClose there.
	useShortcut(
		"esc",
		() => {
			close(true)
		},
		{ enabled: !isDrawer },
	)

	const [legacyVoters] = useQuery(queries.issueUpvoters({ issueId: id }))
	const upvoteCount = countUpvotes(issue?.feedbacks ?? [], legacyVoters)

	const [selectedTab, setTab] = useState<DetailsTabsType>("requests")
	const tab =
		selectedTab === "upvotes" && upvoteCount === 0 ? "requests" : selectedTab
	const [isMoveToPortalOpen, setIsMoveToPortalOpen] = useState(false)

	if (!issue) {
		if (!isQuerySettled(issueResult)) return null
		return <EmptyIssueDetailsScreen />
	}

	const statusIcon = (
		<IssueStatusIcon
			status={issue.status ?? ""}
			color={issue.statusColor ?? ""}
			className="h-3.5 w-3.5 shrink-0"
		/>
	)

	const hasOtherPortals = (workspace?.portalInstances ?? []).length >= 1

	return (
		<>
			{isMoveToPortalOpen && (
				<MoveRequestToPortalDialog
					isOpen
					onClose={() => setIsMoveToPortalOpen(false)}
					request={{
						type: "issue",
						id: issue.id,
						name: issue.title,
						portalInstanceIds: issue.portalInstanceIds ?? [],
					}}
				/>
			)}
			{!isDrawer && <MetaInfo title="Issue Detail" />}
			<DetailTopBar className="justify-between" clearsNavToggle={!isDrawer}>
				<div className="flex min-w-0 items-center gap-1">
					{isDrawer ? (
						<>
							<IconButton
								Icon={XIcon}
								variant="ghost"
								aria-label="Close"
								onClick={() => close()}
							/>
							<div className="flex min-w-0 items-center gap-1.5 px-1 text-sm font-medium text-primary">
								{statusIcon}
								{issue.identifier && (
									<span className="flex-none">{issue.identifier}</span>
								)}
								<span className="truncate">{issue.title}</span>
							</div>
						</>
					) : (
						<DetailBreadcrumb
							parentLabel={previousEntry?.label ?? "Feature requests"}
							onParentClick={() => close()}
							currentIcon={statusIcon}
							currentPrefix={issue.identifier}
							currentLabel={issue.title}
						/>
					)}
					{hasPublicFeatureRequests && (
						<>
							<BreadcrumbSlash className="text-primary" />
							<TitleTabsSwitcher tab={titleTab} onTabChange={setTitleTab} />
						</>
					)}
					<DropdownMenu
						trigger={
							<IconButton
								Icon={MoreVertical}
								variant="ghost"
								iconClassName="fill-current"
							/>
						}
						contentProps={{ side: "bottom", align: "start" }}
					>
						<DropdownMenuGroup>
							{isDrawer && (
								<DropdownMenuItem
									Icon={ExpandIcon as LucideIcon}
									onClick={() =>
										void navigate({ href: detailPath("issue", issue.id) })
									}
								>
									Open full page
								</DropdownMenuItem>
							)}
							{hasOtherPortals && (
								<RoleCheck not={["VIEWER"]}>
									<DropdownMenuItem
										Icon={MoveToPortalIcon}
										onClick={() => setIsMoveToPortalOpen(true)}
									>
										Move to portal
									</DropdownMenuItem>
								</RoleCheck>
							)}
							{issue.url && (
								<DropdownMenuItem
									Icon={LinearIcon as LucideIcon}
									shortcut="L"
									onClick={() => {
										analytics.track("open-issue-in-linear", { id: issue.id })
										if (issue.url) {
											window.open(issue.url, "_blank")
										}
									}}
								>
									Open in Linear
								</DropdownMenuItem>
							)}
							<DropdownMenuItem
								Icon={ClaudeIcon as LucideIcon}
								shortcut="C"
								onClick={openInClaudeCode}
							>
								Open in Claude Code
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<CopyMenuIcon
										strokeWidth={1.75}
										className="mr-2 h-4 w-4 text-secondary"
									/>
									Copy
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										<DropdownMenuItem
											Icon={CopyIdIcon as LucideIcon}
											shortcut="⌘ ."
											onClick={copyIdentifier}
										>
											Issue ID
										</DropdownMenuItem>
										{issue.url && (
											<DropdownMenuItem
												Icon={LinkIcon as LucideIcon}
												shortcut="⌘ ⇧ ,"
												onClick={copyUrl}
											>
												Issue URL
											</DropdownMenuItem>
										)}
										<DropdownMenuItem
											Icon={CodeBranchIcon as LucideIcon}
											shortcut="⌘ ⇧ ."
											onClick={copyBranchName}
											className="w-52"
										>
											Git branch name
										</DropdownMenuItem>
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						</DropdownMenuGroup>
					</DropdownMenu>
				</div>
				<div className="flex-1" />
				<div className="flex items-center gap-1.5">
					{showsPortalVisibility(workspace) && (
						<VisibilityToggle
							type="issue"
							id={issue.id}
							isVisible={issue.isVisible}
							portalInstanceIds={issue.portalInstanceIds ?? []}
							variant="button"
							requests={issue.feedbacks}
						/>
					)}

					{workspace?.domainPublic &&
						workspace.enabledLanguages &&
						workspace.enabledLanguages.length > 1 && (
							<LanguageSelector
								languages={workspace.enabledLanguages}
								selectedLanguage={selectedLanguage}
								onLanguageSelect={setSelectedLanguage}
								variant="code-only"
							/>
						)}

					<RoleCheck not={["VIEWER"]}>
						<NotifyRecipients
							entity={{
								id: issue.id,
								name: issue.publicTitle || issue.title,
								type: "issue",
							}}
						/>
					</RoleCheck>
					<SubscribeButton entityType="issue" entityId={issue.id} />
				</div>
			</DetailTopBar>
			<DetailScreenLayout>
				<DetailScreenEditor
					scrollKey={isDrawer ? `drawer:/issues/${id}` : undefined}
					contentClassName={isDrawer ? "h-auto min-h-full" : undefined}
				>
					<DescriptionSwitch
						item={issue}
						publicDescription={issue?.publicDescription}
						publicTitle={issue?.publicTitle}
						id={issue?.id}
						type="issue"
						tab={titleTab}
						selectedLanguage={editorLanguage}
					/>

					<SubIssuesList issue={issue} />

					<div className="my-6 flex w-full items-center justify-center px-6">
						<div className="w-full border-t" />
					</div>

					<Tabs
						value={tab}
						onValueChange={(value) => setTab(value as DetailsTabsType)}
					>
						<div className="my-4 flex min-h-7 w-full flex-row items-center justify-between px-5 sm:px-8">
							<TabsList className="w-fit justify-start">
								<TabsTrigger value="requests">Requests</TabsTrigger>
								{upvoteCount > 0 && (
									<TabsTrigger value="upvotes" count={upvoteCount}>
										Upvotes
									</TabsTrigger>
								)}
							</TabsList>
							{tab === "requests" &&
								(threadsState.size > 0 ||
									hasActiveDetailFilters(threadControls.filters)) && (
									<DetailThreadsToolbar
										sortOrder={threadControls.sortOrder}
										onSortOrderChange={threadControls.setSortOrder}
										filters={threadControls.filters}
										onFiltersChange={threadControls.setFilters}
										teamMembers={teamMembers}
									/>
								)}
						</div>
						<TabsContent value="requests">
							<DetailThreadsList
								issueId={id}
								setThreadsSize={threadsState.report}
								controls={threadControls}
								hideLabel
							/>
							{threadsState.isEmpty && (
								<div className="flex min-h-[40dvh] w-full items-center justify-center py-8">
									<Empty
										text={
											"No customer requests are linked to this issue yet. Wait for customers to upvote it or link it manually to relevant threads."
										}
										title="Customer requests"
										className="max-w-[20rem]"
										variant="left"
										image={
											<img src={EmptyIssue} alt="Empty customer requests" />
										}
										action={
											<div className="flex items-center gap-4">
												<Button
													onClick={() => {
														void navigate({ to: "/threads" })
													}}
													className="min-w-32"
												>
													Go to Inbox
												</Button>
												<HowItWorksButton
													className="min-h-7"
													article="7e861235-26a4-4b00-9268-a0e82258bbe6"
												/>
											</div>
										}
									/>
								</div>
							)}
						</TabsContent>
						<TabsContent value="upvotes">
							<DetailThreadsList issueId={id} onlyUpvotes hideLabel />
							<DetailUpvotesList issueId={id} hideLabel />
						</TabsContent>
					</Tabs>
				</DetailScreenEditor>
			</DetailScreenLayout>
		</>
	)
}

const EmptyIssueDetailsScreen = () => {
	return (
		<div className="flex w-full flex-1 items-center justify-center">
			<Empty
				text="It may have been moved, deleted, or you may not have access to it."
				title="Issue not found"
				className="max-w-[20rem]"
				variant="center"
				image={<img src={EmptyIssue} alt="Issue not found" />}
			/>
		</div>
	)
}

type LucideIcon = FC<SVGProps<SVGSVGElement>>
